import { useEffect, useState, type ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApprovalChoice, ApprovalProgress } from '@bounded/api-client';
import { api } from '../../api/client';
import { q } from '../../api/queries';
import { useNames } from '../../api/names';
import { Countdown, ErrorNotice, FreshnessLabel, Provenance, VerdictGlyph, type Freshness } from '@bounded/ui/state';
import { Button, Facts, Field, Kbd, Mono, Select, Skeleton } from '@bounded/ui/primitives';
import { cn } from '@bounded/ui/cn';
import { agentName, digestMatches, headline, isWaiting, parseReview, toolName, type Review } from '../../lib/approvals';
import { useIdempotencyKey } from '@bounded/ui/idempotency';
import { useNow } from '@bounded/ui/time';
import { useFormat, useT } from '../../i18n';

export const Route = createFileRoute('/_app/inbox/$deviceId/$approvalId')({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(q.approval(params.deviceId, params.approvalId)),
  component: Detail,
  pendingComponent: () => <div className="grid gap-4 p-8"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-40" /></div>,
});

type Choice = ApprovalChoice['choice'];
const TTLS = [900, 3_600, 4 * 3_600, 24 * 3_600];

function Detail() {
  const t = useT(); const m = t.approval; const f = useFormat();
  const { deviceId, approvalId } = Route.useParams();
  const approval = useQuery(q.approval(deviceId, approvalId));
  const waiting = useQuery(q.waiting());
  const runtime = useQuery(q.runtime(deviceId));
  const names = useNames();
  const a = approval.data;
  if (!a) return approval.error ? <div className="p-8"><ErrorNotice error={approval.error} /></div> : null;

  // Pending on its own isn't enough: the server drops requests whose device was revoked, whose policy changed, or whose task closed.
  const inWaitingSet = waiting.data?.items.some((w) => w.device_id === a.device_id && w.request.approval_id === a.request.approval_id) ?? true;
  const answerable = isWaiting(a) && inWaitingSet;
  const review = parseReview(a.request.summary);
  const device = names.device(a.device_id);
  const who = agentName(a, t.agents) ?? toolName(a, t.inbox.genericTool);

  return (
    <article className="mx-auto max-w-3xl px-4 py-6 sm:px-8 lg:py-8">
      <Link to="/inbox" search={{ show: 'waiting' }} className="mb-3 inline-block text-sm text-graphite hover:text-ink lg:hidden">{m.back}</Link>
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h2 className="min-w-0 font-mono text-lg font-semibold [overflow-wrap:anywhere]">{headline(a)}</h2>
        {answerable && <Countdown until={a.request.expires_at} />}
      </header>
      <p className="mt-1 text-graphite">{m.where(who, device, names.project(a.project_id), <code>{a.task_id}</code>)}</p>

      <div className="mt-5">{review ? <ReviewView review={review} /> : <RawSummary summary={a.request.summary} />}</div>
      <DigestCheck summary={a.request.summary} digest={a.request.review_digest} device={device} />

      {runtime.data && runtime.data.connection !== 'recent' && answerable && (
        <p className="mt-5 rounded-[var(--radius-control)] border border-ask/40 bg-ask/5 px-3 py-2.5">
          <strong className="font-medium">{m.staleTitle(device)}</strong>{' '}
          <span className="text-graphite">{m.staleBody}</span>
        </p>
      )}

      <div className="mt-6">
        {answerable ? <Decide a={a} onRefresh={() => void approval.refetch()} />
          : a.status === 'pending' && isWaiting(a) ? <p className="text-graphite">{m.unanswerable}</p>
          : <Outcome a={a} />}
      </div>

      <details className="group mt-8 border-t border-rule pt-4">
        <summary className="cursor-pointer text-graphite select-none hover:text-ink">{m.details}</summary>
        <div className="mt-4">
          <Facts items={[
            [m.facts.device, <span className="inline-flex flex-wrap gap-x-3">{device} {runtime.data && <FreshnessLabel freshness={runtime.data.connection as Freshness} at={runtime.data.received_at} />}</span>],
            ...(a.origin ? [[m.facts.agent, <span>{m.agentVersion(a.origin.agent.name, a.origin.agent.version)} <code className="text-graphite">{a.origin.adapter.capability_profile}</code></span>] as [string, ReactNode]] : []),
            [m.facts.requirement, a.request.requirement_ids.map((r) => <code key={r} className="mr-2">{r}</code>)],
            [m.facts.binding, <Mono value={a.request.binding_hash} copy />],
            [m.facts.policy, <Mono value={a.request.policy_hash} copy />],
            [m.facts.reviewed, <Mono value={a.request.review_digest} copy />],
            [m.facts.requested, f.dateTime(a.request.created_at)],
            [m.facts.expires, f.dateTime(a.request.expires_at)],
          ]} />
          <p className="mt-4 max-w-[70ch] text-sm text-graphite">{m.boundNote}</p>
        </div>
      </details>
    </article>
  );
}

/** Structured view of the device's review payload. Every string is device-written and rendered as text, never translated. */
function ReviewView({ review }: { review: Review }) {
  const t = useT(); const m = t.approval;
  const args = Object.keys(review.tool.arguments).length ? JSON.stringify(review.tool.arguments, null, 2) : null;
  return (
    <div className="grid gap-4 rounded-[var(--radius-panel)] border border-rule bg-surface p-4">
      <div>
        <p className="text-sm text-graphite">{m.affects}</p>
        <ul className="mt-1.5 grid gap-1.5">
          {review.operations.map((op, i) => (
            <li key={i} className="grid gap-x-3 sm:grid-cols-[9rem_1fr]">
              <span className="text-graphite">{m.kinds[op.kind] ?? op.kind}</span>
              <span className="min-w-0"><code className="[overflow-wrap:anywhere]">{op.operation}</code> <span className="font-mono text-sm [overflow-wrap:anywhere]">{op.target.locator}</span>
                {op.target.environment && <span className="ml-2 rounded bg-ask/10 px-1.5 text-sm text-ask">{op.target.environment}</span>}</span>
            </li>
          ))}
        </ul>
      </div>
      {args && (
        <div>
          <p className="text-sm text-graphite">{m.arguments(<code>{review.tool.name}</code>)}</p>
          <pre className="mt-1.5 max-h-80 overflow-auto rounded-[var(--radius-control)] bg-sunken p-3 font-mono text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">{args}</pre>
        </div>
      )}
    </div>
  );
}
function RawSummary({ summary }: { summary: string }) {
  const t = useT();
  return (
    <figure>
      <pre className="overflow-x-auto rounded-[var(--radius-panel)] border border-rule bg-surface p-4 font-mono text-sm leading-relaxed whitespace-pre-wrap">{summary}</pre>
      <figcaption className="mt-1.5 text-sm text-graphite">{t.approval.raw}</figcaption>
    </figure>
  );
}
/** The device checks review_digest before acting on an answer; checking it here too means what you read is what you approve. */
function DigestCheck({ summary, digest, device }: { summary: string; digest: string; device: string }) {
  const t = useT();
  const [ok, setOk] = useState<boolean | null>(null);
  useEffect(() => { let live = true; void digestMatches(summary, digest).then((v) => { if (live) setOk(v); }); return () => { live = false; }; }, [summary, digest]);
  if (ok === null) return null;
  return ok
    ? <p className="mt-1.5 text-sm text-graphite">{t.approval.digestOk(device)}</p>
    : <p role="alert" className="mt-2 rounded-[var(--radius-control)] border border-deny/40 bg-deny/5 px-3 py-2 text-deny">{t.approval.digestBad(device)}</p>;
}

function Decide({ a, onRefresh }: { a: ApprovalProgress; onRefresh: () => void }) {
  const t = useT(); const m = t.approval;
  const queryClient = useQueryClient();
  const keyFor = useIdempotencyKey();
  const [taskOpen, setTaskOpen] = useState(false);
  const [maxUses, setMaxUses] = useState(5);
  const [ttl, setTtl] = useState(3_600);

  const respond = useMutation({
    mutationFn: (choice: Choice) => {
      const base = { expected_revision: a.request.revision, binding_hash: a.request.binding_hash, policy_hash: a.request.policy_hash, review_digest: a.request.review_digest };
      const body: ApprovalChoice = choice === 'allow_task' ? { ...base, choice, task_grant: { max_uses: maxUses, ttl_seconds: ttl } } : { ...base, choice };
      return api.respond(a.device_id, a.request.approval_id, body, keyFor(body));
    },
    onSuccess: (view) => {
      queryClient.setQueryData(q.approval(a.device_id, a.request.approval_id).queryKey, (old) => old && { ...old, ...view });
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });

  // Single-key answers. Allow-for-task opens its form first rather than firing with defaults.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || respond.isPending || (e.target instanceof HTMLElement && e.target.closest('input,textarea,select'))) return;
      if (e.key === 'd') respond.mutate('deny');
      if (e.key === 'a') respond.mutate('allow_once');
      if (e.key === 't') setTaskOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [respond]);

  return (
    <section aria-label={m.details} className="grid gap-4">
      {respond.error && <ErrorNotice error={respond.error} onRetry={() => respond.variables && respond.mutate(respond.variables)} onRefresh={onRefresh} />}
      <div className="flex flex-wrap gap-2">
        <Button variant="danger" disabled={respond.isPending} onClick={() => respond.mutate('deny')}><VerdictGlyph outcome="DENY" />{m.deny} <Kbd>d</Kbd></Button>
        <Button disabled={respond.isPending} onClick={() => respond.mutate('allow_once')}><VerdictGlyph outcome="ALLOW" />{m.allowOnce} <Kbd>a</Kbd></Button>
        <Button variant={taskOpen ? 'ghost' : 'secondary'} disabled={respond.isPending} aria-expanded={taskOpen} onClick={() => setTaskOpen((v) => !v)}>{m.allowTask} <Kbd>t</Kbd></Button>
      </div>
      {taskOpen && (
        <form className="grid gap-4 rounded-[var(--radius-panel)] border border-rule bg-surface p-4" onSubmit={(e) => { e.preventDefault(); respond.mutate('allow_task'); }}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={m.howMany} htmlFor="uses"><Select id="uses" value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))}>{[1, 3, 5, 10, 25].map((n) => <option key={n} value={n}>{m.uses(n)}</option>)}</Select></Field>
            <Field label={m.howLong} htmlFor="ttl"><Select id="ttl" value={ttl} onChange={(e) => setTtl(Number(e.target.value))}>{TTLS.map((s) => <option key={s} value={s}>{m.ttl[s]}</option>)}</Select></Field>
          </div>
          <p className="max-w-[70ch] text-graphite">{m.taskScope(a.request.requirement_ids.join(', '), <code>{a.task_id}</code>, maxUses, m.ttl[ttl])}</p>
          <div><Button type="submit" variant="primary" disabled={respond.isPending}>{m.allowTaskSubmit}</Button></div>
        </form>
      )}
    </section>
  );
}

/** After an answer: what the cloud recorded vs. what the device has reported, as a sequence. */
function Outcome({ a }: { a: ApprovalProgress }) {
  const t = useT(); const m = t.approval.outcome; const f = useFormat();
  const now = useNow(2_000);
  const waiting = useQuery(q.waiting());
  const next = waiting.data?.items.find((w) => isWaiting(w, now) && !(w.device_id === a.device_id && w.request.approval_id === a.request.approval_id));
  const nextLink = next && (
    <Link to="/inbox/$deviceId/$approvalId" params={{ deviceId: next.device_id, approvalId: next.request.approval_id }} search={{ show: 'waiting' }} className="mt-4 inline-block text-boundary hover:underline">
      {m.next(<code>{headline(next)}</code>)}
    </Link>
  );
  if (a.status === 'expired' || a.status === 'cancelled' || (a.status === 'pending' && !isWaiting(a, now))) {
    return <div><p className="text-graphite">{a.status === 'cancelled' ? m.cancelled : m.expired}</p>{nextLink}</div>;
  }
  const denied = a.response?.choice === 'deny';
  const exec = a.execution?.receipt.status;
  const steps: { label: string; detail?: string; state: 'done' | 'waiting' | 'failed' }[] = [
    { label: denied ? m.denied : a.response?.choice === 'allow_task' ? m.allowedTask : m.allowedOnce, detail: a.response ? m.inConsole(f.ago(a.response.responded_at, now)) : undefined, state: 'done' },
    { label: m.received, detail: a.device_state ? f.ago(a.device_state.received_at, now) : m.waitingDevice, state: a.device_state ? (a.device_state.receipt.status === 'accepted' ? 'done' : 'failed') : 'waiting' },
    denied
      ? { label: m.blocked, detail: a.device_state ? m.blockedDetail : undefined, state: a.device_state ? 'done' : 'waiting' }
      : { label: !a.execution ? m.runs : exec === 'failed' ? m.failed : exec === 'outcome_unknown' ? m.unknown : m.ran,
        detail: a.execution ? f.ago(a.execution.received_at, now) : m.notReported, state: !a.execution ? 'waiting' : exec === 'succeeded' || exec === 'consumed' ? 'done' : 'failed' },
  ];
  return (
    <section>
      <ol className="grid gap-0">
        {steps.map((s, i) => (
          <li key={i} className="grid grid-cols-[1.5rem_1fr] gap-3">
            <div className="flex flex-col items-center">
              <span aria-hidden className={cn('mt-1 size-3.5 rounded-full', s.state === 'done' && 'bg-ink', s.state === 'waiting' && 'border border-dashed border-graphite', s.state === 'failed' && 'bg-deny')} />
              {i < steps.length - 1 && <span aria-hidden className={cn('w-px flex-1', steps[i + 1].state === 'waiting' ? 'border-l border-dashed border-rule-strong' : 'bg-ink')} />}
            </div>
            <div className="pb-4">
              <p className={cn('font-medium', s.state === 'waiting' && 'text-graphite')}>{s.label}</p>
              {s.detail && <p className="text-sm text-graphite">{s.detail}</p>}
            </div>
          </li>
        ))}
      </ol>
      <Provenance>{m.provenance}</Provenance>
      {nextLink}
    </section>
  );
}
